import { StyledModalFooter } from "app/pages/sales-intelligence/sub-modules/signals/components/modals/SignalsModal/styles";
import useTrafficSignalFiltersViewResolver from "app/pages/sales-intelligence/sub-modules/signals/hooks/useTrafficSignalFiltersViewResolver";
import type SignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalFilters";
import type SignalTemplate from "app/pages/sales-intelligence/sub-modules/signals/models/templates/SignalTemplate";
import type TrafficSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficSignalFilters";
import type TrafficSignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficSignalSubType";
import { isTrafficSignalFilters } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficSignalFilters";
import type SignalFiltersViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewProps";
import { createElement, useCallback, useMemo } from "react";
import type { ICountryObject } from "app/services/CountryService";
import useSignalFiltersState from "../../../hooks/useFiltersState";
import { StyledFiltersViewContent, StyledFiltersViewInner, StyledNotificationWrapper } from "../../styles";
import TrafficSignalFiltersViewHeader from "./TrafficSignalFiltersViewHeader/TrafficSignalFiltersViewHeader";

type TrafficSignalFiltersViewProps = SignalFiltersViewProps<Partial<TrafficSignalFilters>> & {
    countries: readonly ICountryObject[];
};

const TrafficSignalFiltersView = (props: TrafficSignalFiltersViewProps) => {
    const { currentFilters, setCurrentFilters } = useSignalFiltersState(props.filters, props.onFiltersChange);

    const filtersViewResolver = useTrafficSignalFiltersViewResolver({ countries: props.countries });

    const FiltersView = useMemo(() => {
        return filtersViewResolver.getFiltersView(props.signalSubType as TrafficSignalSubType);
    }, [filtersViewResolver, props.signalSubType]);

    const handleTemplateChange = useCallback((template: Readonly<SignalTemplate<SignalFilters>>): void => {
        setCurrentFilters(template.filters);
    }, []);

    function handleChange(updatedFilters: Partial<TrafficSignalFilters>) {
        setCurrentFilters((prevFilters) => ({ ...prevFilters, ...updatedFilters }));
    }

    function handleSubmit() {
        props.onFiltersSubmit(currentFilters);
    }

    return (
        <StyledFiltersViewInner alignItems="center">
            <StyledFiltersViewContent>
                <TrafficSignalFiltersViewHeader
                    signalSubType={props.signalSubType}
                    renderTemplateView={props.renderTemplateView}
                    onTemplateSelect={handleTemplateChange}
                />

                {/* FiltersView is resolved dynamically per sub-type and may be created fresh on
                    each resolution, so it can't be expressed as a static JSX tag; the useMemo
                    above keeps it stable. */}
                {FiltersView
                    ? createElement(FiltersView, {
                          key: props.signalAlias,
                          filters: currentFilters,
                          signalAlias: props.signalAlias,
                          signalSubType: props.signalSubType as TrafficSignalSubType,
                          onFiltersChange: handleChange,
                      })
                    : null}

                {props.notificationView ? (
                    <StyledNotificationWrapper>{props.notificationView}</StyledNotificationWrapper>
                ) : null}
            </StyledFiltersViewContent>
            <StyledModalFooter>
                {props.renderSubmit({
                    isDisabled: !isTrafficSignalFilters(currentFilters),
                    onSubmit: handleSubmit,
                })}
            </StyledModalFooter>
        </StyledFiltersViewInner>
    );
};

export default TrafficSignalFiltersView;
